import { Dispatch, useEffect } from 'react'
import { toast } from 'react-toastify'
import { API } from 'src/services/config'
import { AuthAction } from '../reducers/auth.reducer'
import { refreshSession } from '../services/auth.service'
import { storageService } from '../services/storage.service'
import { calculateTokenExpiry, isTokenValid } from '../services/token.service'

interface UseAuthInitializeProps {
  dispatch: Dispatch<AuthAction>
  scheduleTokenRefresh: () => void
}

export function useAuthInitialize({ dispatch, scheduleTokenRefresh }: UseAuthInitializeProps) {
  useEffect(() => {
    let mounted = true

    const initializeAuth = async () => {
      const token = storageService.getToken()
      const refreshToken = storageService.getRefreshToken()
      const user = storageService.getUser()
      const baseFarm = storageService.getFarmConfig()

      if (!token) {
        if (mounted) {
          dispatch({ type: 'SET_LOADING', payload: false })
        }
        return
      }

      if (!isTokenValid()) {
        if (refreshToken) {
          try {
            const response = await refreshSession(refreshToken)
            const newToken = response.authenticationResult.accessToken
            const newRefreshToken = response.authenticationResult.refreshToken
            const expiresIn = calculateTokenExpiry(response.authenticationResult.expiresIn)

            if (user) {
              storageService.saveSession({
                user,
                token: newToken,
                refreshToken: newRefreshToken,
                expiresIn: expiresIn.toString(),
                farmConfig: baseFarm!
              })
            }

            API.defaults.headers['Authorization'] = `Bearer ${newToken}`
            scheduleTokenRefresh()

            if (mounted) {
              dispatch({
                type: 'HYDRATE_SESSION',
                payload: {
                  user,
                  farmConfig: baseFarm!,
                  token: newToken
                }
              })
            }

            return
          } catch {
            storageService.clearSession()

            if (mounted) {
              dispatch({ type: 'SESSION_EXPIRED' })
            }

            toast.info('Sessão expirada')
            return
          }
        }

        storageService.clearSession()

        if (mounted) {
          dispatch({ type: 'SESSION_EXPIRED' })
        }

        toast.info('Sessão expirada')
        return
      }

      API.defaults.headers['Authorization'] = `Bearer ${token}`
      scheduleTokenRefresh()

      if (mounted) {
        dispatch({
          type: 'HYDRATE_SESSION',
          payload: {
            user,
            token,
            farmConfig: baseFarm!
          }
        })
      }
    }

    void initializeAuth()

    return () => {
      mounted = false
    }
  }, [dispatch, scheduleTokenRefresh])
}
